import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { ShareScan, TreatmentTrackerIcon } from 'components/common/icons'
import Autochart from 'components/common/icons/scan-item-actions/Autochart'
import SendScanReview from 'components/common/icons/scan-item-actions/SendScanReview'
import ShareProgress from 'components/common/icons/scan-item-actions/ShareProgress'
import Tooltip from 'components/common/Tooltip'
import { TreatmentTypes } from 'consts/treatmentConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useRolePermissions from 'hooks/useRolePermissions'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { isUserOfAnyAdminRole } from 'utils/authUtils'
import { isMobile } from 'utils/mobileUtils'
import { calculateAge } from 'utils/patientUtils'
import { isScanEligableForBeforeAfter, scanHasScanSummaryCenterPose } from 'utils/scanUtils'
import { isRetentionStamp, readTrackingStamp } from 'components/Patients/TreatmentTracker/trackingStampUtils'
import Actions from '../../../../actions'
import ScanActionButton from '../ScanActionButton'

const useStyles = makeStyles(theme => ({
  actionsContainer: {
    marginBottom: 20,
    lineHeight: 0
  },
  buttonContainer: {
    height: '27px',
    '&.withBorder': {
      borderRight: '1px solid var(--border-color-10)'
    }
  }
}))

const ScanItemActions = ({ scanIndex, scan }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { permissions } = useRolePermissions()
  const dispatch = useDispatch()
  const {
    autochart: autochartFF,
    treatmentTracker: treatmentTrackerFF,
    treatmentTrackerBraces: treatmentTrackerBracesFF,
    treatmentTrackerRetention: treatmentTrackerRetentionFF
  } = useFeatureFlags()
  const { isChurnedPlan } = usePlanLimits()

  const patient = useSelector(state => state.patientsReducer.patient)
  const treatment = useSelector(state => state.treatmentReducer.treatment)
  const { grinScans: patientScans } = useSelector(state => state.treatmentReducer)

  const isPatientActive = useMemo(
    () => patient.user?.isActive && !treatment?.currentStatus?.statusKey?.includes('transferred'),
    [patient, treatment]
  )

  const grinScansForItem = useMemo(
    () => (scan?.scanPairId ? patientScans?.filter(s => s.scanPairId === scan.scanPairId) : [scan]),
    [patientScans, scan]
  )

  const autochartScan = useMemo(() => {
    const scans = grinScansForItem || []
    const hasWithAligner = scans.some(s => !!s.withAligner)
    const hasWithoutAligner = scans.some(s => !s.withAligner)

    if (hasWithAligner && hasWithoutAligner) {
      return scans.find(s => !s.withAligner && s.autochartId)
    }

    if (hasWithAligner && !hasWithoutAligner) {
      return null
    }

    return scans.find(s => s.autochartId)
  }, [grinScansForItem])

  const actions = useMemo(
    () =>
      [
        {
          icon: <SendScanReview />,
          label: scan.scanReviews?.items?.length
            ? t('pages.patients.selectedPatient.timeline.actions.sendAnotherReview')
            : t('pages.patients.selectedPatient.timeline.actions.sendReview'),
          onClick: () => dispatch(Actions.openScanReviewEditor(scan)),
          visible: !isChurnedPlan && !isMobile() && permissions.recordScanReviews && isPatientActive,
          elementId: `scan-review`
        },
        {
          icon: <ShareProgress />,
          label: t('pages.patients.selectedPatient.timeline.actions.shareProgress'),
          onClick: () =>
            dispatch(
              Actions.toggleBeforeAfterDialog({
                open: true,
                scanId: scan.id,
                patientId: patient.id,
                analytics: {
                  source: 'timeline',
                  scanIndex
                }
              })
            ),
          visible:
            !isChurnedPlan &&
            scanIndex > 1 &&
            isPatientActive &&
            isScanEligableForBeforeAfter(scan) &&
            permissions.shareScanProgress,
          elementId: `share-progress`
        },
        (() => {
          const openAutochart = () =>
            dispatch(
              Actions.autochartToggleDialog({
                isOpen: true,
                grinScanId: autochartScan.id,
                patientId: scan.patientId
              })
            )

          const { age } = calculateAge({
            birthdate: patient?.details?.birthdate,
            ageRange: patient?.details?.ageRange
          })
          const ageRange = patient?.details?.ageRange
          const isUnder13Range = typeof ageRange === 'string' && ageRange.toLowerCase().includes('under 13')
          const shouldGateAutochart = (age !== null && age < 14) || isUnder13Range

          const isInternal = isUserOfAnyAdminRole()

          const tooltipMessage = t(
            'pages.patients.selectedPatient.timeline.actions.autochartDisabledUnder14',
            'Autochart is not available for patients under the age of 14'
          )

          const tooltip = shouldGateAutochart ? (
            <span>
              {tooltipMessage}
              {isInternal && (
                <>
                  {' '}
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={e => {
                      e.stopPropagation()
                      openAutochart()
                    }}
                  >
                    {t('general.showAnyway', 'Show Anyway')}
                  </span>
                </>
              )}
            </span>
          ) : undefined

          return {
            icon: <Autochart />,
            label: t('pages.patients.selectedPatient.timeline.actions.autochart'),
            onClick: openAutochart,
            visible: !isMobile() && autochartFF && autochartScan,
            elementId: 'autochart',
            disabled: !!shouldGateAutochart,
            tooltip,
            tooltipInteractive: !!shouldGateAutochart && !!isInternal
          }
        })(),
        {
          icon: <ShareScan />,
          label: t('pages.patients.selectedPatient.timeline.actions.shareScan'),
          onClick: () => {
            trackEvent('Share scan modal - opened', {
              analyticSource: 'Timeline'
            })
            dispatch(Actions.toggleShareNewScanModal({ isOpen: true, grinScanId: scan.id }))
          },
          visible: !isChurnedPlan && permissions.shareScan && scanHasScanSummaryCenterPose(scan),
          elementId: 'shareScan'
        },
        {
          icon: <TreatmentTrackerIcon />,
          label: t('pages.patients.selectedPatient.timeline.actions.treatmentTracker'),
          onClick: () => {
            trackEvent('Treatment tracker popup - opened', {
              grinScanId: scan.id,
              patientId: patient.id,
              ...readTrackingStamp(scan.treatmentTrackerData)
            })
            dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: true, grinScanId: scan.id }))
          },
          visible:
            !isMobile() &&
            ((treatment?.type === TreatmentTypes.Aligners && treatmentTrackerFF) ||
              (treatment?.type === TreatmentTypes.Braces && treatmentTrackerBracesFF) ||
              // A retention result is stamped on the scan itself; the treatment-type flags
              // gate the active plan only. Same rule as the V2 timeline.
              (treatmentTrackerRetentionFF && isRetentionStamp(readTrackingStamp(scan.treatmentTrackerData)))) &&
            scan.scanSummaryStatus === 'completed' &&
            scan.treatmentTrackerStatus === 'completed' &&
            !!scan.treatmentTrackerStatus,
          elementId: 'treatmentTracker'
        },
        {
          label: t('pages.patients.selectedPatient.timeline.actions.regenerateScan'),
          onClick: () => {
            dispatch(Actions.qaToggleRegenerateScanModal({ isOpen: true, grinScanId: scan.id }))
          },
          visible: !isMobile() && permissions.regenerateScan,
          elementId: 'regenerateScan'
        }
      ].filter(action => action.visible),
    [
      scan,
      t,
      permissions,
      isPatientActive,
      scanIndex,
      autochartFF,
      dispatch,
      patient,
      isChurnedPlan,
      treatment,
      treatmentTrackerBracesFF,
      treatmentTrackerFF,
      treatmentTrackerRetentionFF,
      autochartScan
    ]
  )

  return (
    <Grid container justifyContent="flex-start" alignItems="center" className={classes.actionsContainer}>
      {actions.map((action, i) => (
        <Grid
          item
          key={`scan-action-${i}`}
          className={[classes.buttonContainer, i < actions.length - 1 ? 'withBorder' : ''].join(' ')}
        >
          <Tooltip value={action.tooltip || action.label} placement="bottom" interactive={!!action.tooltipInteractive}>
            <ScanActionButton
              label={action.label}
              icon={action.icon}
              onClick={action.onClick}
              className={action.elementId}
              disabled={!!action.disabled}
            >
              {action.label}
            </ScanActionButton>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  )
}

export default ScanItemActions
